import { useEffect, useId, useState } from 'react';
import type { ProductId } from '../content/products';
import { appIcons } from '../content/appIcons';

/**
 * The hero illustration: the logo's idea drawn large. One core, five traces;
 * the three solid ones end at a product's app icon, the two dashed ones are the
 * products still to come. Signal pulses travel each live trace unless the
 * visitor has asked for reduced motion.
 */
type Branch =
  | { d: string; end: readonly [number, number]; live: true; id: ProductId; name: string; label: string }
  | { d: string; end: readonly [number, number]; live: false };

const ICON = 52;

const branches: readonly Branch[] = [
  { d: 'M128 204 L272 60 H394', end: [420, 60], live: true, id: 'veriplus', name: 'VeriPlus', label: 'Verification' },
  { d: 'M132 212 L204 140 H372', end: [372, 140], live: false },
  { d: 'M136 220 H394', end: [420, 220], live: true, id: 'tambo', name: 'Tambo', label: 'Phone security' },
  { d: 'M132 228 L204 300 H372', end: [372, 300], live: false },
  { d: 'M128 236 L272 380 H394', end: [420, 380], live: true, id: 'sassy', name: 'Sassy', label: 'Networking' },
];

function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches,
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export function HeroNetwork() {
  const uid = useId().replace(/:/g, '');
  const reduced = usePrefersReducedMotion();

  return (
    <svg
      className="hero-net"
      viewBox="0 0 620 440"
      role="img"
      aria-label="Diagram: the Divergent Networks core connected to its products VeriPlus, Tambo and Sassy, with more to come."
    >
      <defs>
        <radialGradient id={`${uid}-glow`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#0A84FF" stopOpacity="0.45" />
          <stop offset="1" stopColor="#0A84FF" stopOpacity="0" />
        </radialGradient>
        <clipPath id={`${uid}-tile`} clipPathUnits="objectBoundingBox">
          <rect width="1" height="1" rx="0.225" />
        </clipPath>
        <filter id={`${uid}-lift`} x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#000814" floodOpacity="0.55" />
        </filter>
      </defs>

      <circle cx="90" cy="220" r="110" fill={`url(#${uid}-glow)`} />

      {branches.map((b, i) => (
        <g key={b.d}>
          <path
            id={`${uid}-p${i}`}
            className={`hero-net__trace${b.live ? '' : ' hero-net__trace--next'}`}
            d={b.d}
            style={{ animationDelay: `${0.15 * i}s` }}
          />
          {b.live ? (
            <g className="hero-net__label">
              <g filter={`url(#${uid}-lift)`}>
                <image
                  href={appIcons[b.id]}
                  x={b.end[0] - ICON / 2}
                  y={b.end[1] - ICON / 2}
                  width={ICON}
                  height={ICON}
                  clipPath={`url(#${uid}-tile)`}
                  preserveAspectRatio="xMidYMid slice"
                />
              </g>
              <rect
                className="hero-net__tile-edge"
                x={b.end[0] - ICON / 2 + 0.5}
                y={b.end[1] - ICON / 2 + 0.5}
                width={ICON - 1}
                height={ICON - 1}
                rx={ICON * 0.225}
              />
              <text x={b.end[0] + 42} y={b.end[1] - 2} className="hero-net__name">
                {b.name}
              </text>
              <text x={b.end[0] + 42} y={b.end[1] + 17} className="hero-net__cat">
                {b.label.toUpperCase()}
              </text>
            </g>
          ) : (
            <>
              <circle className="hero-net__node hero-net__node--next" cx={b.end[0]} cy={b.end[1]} r={7} />
              <text x={b.end[0] + 18} y={b.end[1] + 5} className="hero-net__cat hero-net__cat--next">
                NEXT
              </text>
            </>
          )}
          {b.live && !reduced && (
            // Hidden until its motion begins, or it would sit at the origin.
            <circle r="4" className="hero-net__pulse" opacity="0">
              <set attributeName="opacity" to="1" begin={`${1.6 + i * 0.5}s`} />
              <animateMotion dur="3.2s" begin={`${1.6 + i * 0.5}s`} repeatCount="indefinite">
                <mpath href={`#${uid}-p${i}`} />
              </animateMotion>
            </circle>
          )}
        </g>
      ))}

      <path className="hero-net__core" d="M90 172 L131.6 196 V244 L90 268 L48.4 244 V196 Z" />
      <path className="hero-net__core-inner" d="M90 196 L110.8 208 V232 L90 244 L69.2 232 V208 Z" />
    </svg>
  );
}
